import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';

const AnimatedQRCode = ({ value, size = 200, className = "", onComplete }) => {
    const canvasRef = useRef(null);
    const [particles, setParticles] = useState([]);
    const [isAnimating, setIsAnimating] = useState(true);
    const [showQR, setShowQR] = useState(false);
    const animationRef = useRef(null);
    const qrGeneratedRef = useRef(false);

    // Generate QR code data once
    useEffect(() => {
        if (!value || qrGeneratedRef.current) return;

        const generateQRData = async () => {
            try {
                qrGeneratedRef.current = true;

                // Create a temporary canvas to get QR code matrix
                const tempCanvas = document.createElement('canvas');
                await QRCode.toCanvas(tempCanvas, value, {
                    width: size,
                    margin: 1,
                    color: {
                        dark: '#000000',
                        light: '#ffffff'
                    }
                });

                const ctx = tempCanvas.getContext('2d');
                const imageData = ctx.getImageData(0, 0, size, size);

                // Create particles from QR code data (ultra optimized)
                createParticlesFromQR(imageData, size);

                // Start animation immediately
                startAnimation();
            } catch (error) {
                console.error('Error generating QR code:', error);
                // Fallback: show QR code immediately
                setShowQR(true);
                setIsAnimating(false);
                if (onComplete) onComplete();
            }
        };

        generateQRData();

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [value, size, onComplete]);

    // Ultra optimized particle creation - minimal particles for speed
    const createParticlesFromQR = (imageData, canvasSize) => {
        const { data, width } = imageData;
        const newParticles = [];
        const cellSize = 12; // Much larger cell size for fewer particles
        const cols = Math.floor(width / cellSize);
        const rows = Math.floor(width / cellSize);

        // Single starting position - center of canvas (for faster assembly)
        const centerX = canvasSize / 2;
        const centerY = canvasSize / 2;

        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                // Check if this cell contains a black pixel
                const x = col * cellSize + cellSize / 2;
                const y = row * cellSize + cellSize / 2;
                const index = (Math.floor(y) * width + Math.floor(x)) * 4;

                // If pixel is black (QR code module)
                if (data[index] < 128) {
                    // Create single particle per cell
                    newParticles.push({
                        id: `${row}-${col}`,
                        targetX: col * cellSize + cellSize / 2,
                        targetY: row * cellSize + cellSize / 2,
                        currentX: centerX + (Math.random() - 0.5) * 50, // Start near center with slight randomness
                        currentY: centerY + (Math.random() - 0.5) * 50,
                        speed: 0.1 + Math.random() * 0.15, // Very fast speed
                        delay: Math.random() * 100, // Minimal delay (max 0.1 seconds)
                        startTime: Date.now() + Math.random() * 100
                    });
                }
            }
        }

        setParticles(newParticles);
    };

    // Ultra fast animation loop
    const startAnimation = () => {
        const animate = () => {
            setParticles(prevParticles => {
                let allArrived = true;

                const updatedParticles = prevParticles.map(particle => {
                    // Check if particle should start moving
                    if (Date.now() < particle.startTime) {
                        allArrived = false;
                        return particle;
                    }

                    // Calculate movement
                    const dx = particle.targetX - particle.currentX;
                    const dy = particle.targetY - particle.currentY;
                    const distance = Math.sqrt(dx * dx + dy * dy);

                    if (distance < 2) {
                        // Particle reached target
                        return {
                            ...particle,
                            currentX: particle.targetX,
                            currentY: particle.targetY
                        };
                    }

                    allArrived = false;

                    // Move particle towards target (extremely fast)
                    const speed = particle.speed * 15; // Very high speed multiplier
                    const angle = Math.atan2(dy, dx);

                    return {
                        ...particle,
                        currentX: particle.currentX + Math.cos(angle) * Math.min(speed, distance),
                        currentY: particle.currentY + Math.sin(angle) * Math.min(speed, distance)
                    };
                });

                // Check if all particles have arrived
                if (allArrived && !showQR) {
                    // Show QR code immediately
                    setShowQR(true);
                    setIsAnimating(false);
                    if (onComplete) onComplete();
                }

                return updatedParticles;
            });

            // Continue animation if not showing QR yet
            if (!showQR) {
                animationRef.current = requestAnimationFrame(animate);
            }
        };

        animationRef.current = requestAnimationFrame(animate);
    };

    // Draw particles on canvas
    useEffect(() => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Clear canvas
        ctx.clearRect(0, 0, size, size);

        if (showQR) {
            // Draw final QR code
            QRCode.toCanvas(canvas, value, {
                width: size,
                margin: 1,
                color: {
                    dark: '#000000',
                    light: '#ffffff'
                }
            });
        } else {
            // Draw particles with trail effect for speed lines
            particles.forEach(particle => {
                // Draw particle
                ctx.beginPath();
                ctx.arc(particle.currentX, particle.currentY, 3, 0, Math.PI * 2);

                // Calculate velocity for trail effect
                const dx = particle.targetX - particle.currentX;
                const dy = particle.targetY - particle.currentY;
                const velocity = Math.sqrt(dx * dx + dy * dy);

                // Draw trail line
                if (velocity > 5) {
                    ctx.beginPath();
                    ctx.moveTo(particle.currentX, particle.currentY);
                    ctx.lineTo(
                        particle.currentX - dx * 0.2,
                        particle.currentY - dy * 0.2
                    );
                    ctx.strokeStyle = 'rgba(59, 130, 246, 0.3)';
                    ctx.lineWidth = 1;
                    ctx.stroke();
                }

                // Gradient fill for particles
                const gradient = ctx.createRadialGradient(
                    particle.currentX - 2, particle.currentY - 2, 1,
                    particle.currentX, particle.currentY, 5
                );
                gradient.addColorStop(0, '#60a5fa');
                gradient.addColorStop(1, '#2563eb');

                ctx.fillStyle = gradient;
                ctx.shadowColor = '#3b82f6';
                ctx.shadowBlur = 10;
                ctx.fill();
            });

            // Reset shadow
            ctx.shadowBlur = 0;
        }
    }, [particles, showQR, size, value]);

    return (
        <div className={`relative ${className}`}>
            <canvas
                ref={canvasRef}
                width={size}
                height={size}
                className="rounded-lg shadow-lg"
            />
        </div>
    );
};

export default AnimatedQRCode;